import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Check, ChevronRight, Search, UserPlus, ShieldAlert, Users } from "lucide-react";
import TeamSelector from "@/components/TeamSelector";

const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
const PROVINCES = ["AB", "BC", "MB", "NB", "NL", "NS", "NT", "NU", "ON", "PE", "QC", "SK", "YT"];

export default function CampRegister() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [step, setStep] = useState(1); // 1: player, 2: waiver, 3: payment
  const [camp, setCamp] = useState(null);
  const [myPlayers, setMyPlayers] = useState([]);
  const [selectedPlayerId, setSelectedPlayerId] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [registrationId, setRegistrationId] = useState(null);

  // existing-player search
  const [searchQ, setSearchQ] = useState("");
  const [searchResults, setSearchResults] = useState(null); // null = not searched yet
  const [searching, setSearching] = useState(false);

  // new player form
  const [showNewPlayer, setShowNewPlayer] = useState(false);
  const [np, setNp] = useState({
    first_name: "",
    last_name: "",
    graduation_year: "",
    primary_position: "",
    secondary_position: "",
    bats: "",
    throws: "",
    province: "",
    city: "",
    school: "",
    current_team: "",
    current_team_id: "",
    show_age_publicly: false,
    date_of_birth: "",
  });

  // waiver
  const [waiver, setWaiver] = useState({
    signer_name: "",
    signer_relationship: "parent",
    signature_text: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    agreed: false,
  });

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/signup", { state: { redirectTo: `/camps/${slug}/register` } });
      return;
    }
    api.get(`/camps/${slug}`).then(({ data }) => setCamp(data));
    api.get("/players/my").then(({ data }) => {
      const items = data.items || [];
      setMyPlayers(items);
      if (items.length === 1) { setSelectedPlayerId(items[0].id); setSelectedPlayer(items[0]); }
    });
  }, [user, loading, slug, navigate]);

  // Debounced type-ahead search — never preloads the DB (min 2 chars).
  useEffect(() => {
    const term = searchQ.trim();
    if (term.length < 2) { setSearchResults(null); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.get("/players/registration-search", { params: { q: term } })
        .then(({ data }) => setSearchResults(data.results || []))
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [searchQ]);

  const selectPlayer = (p) => { setSelectedPlayerId(p.id); setSelectedPlayer(p); setError(""); };

  const openCreateFromSearch = () => {
    const parts = searchQ.trim().split(/\s+/);
    setNp((n) => ({ ...n, first_name: n.first_name || parts[0] || "", last_name: n.last_name || parts.slice(1).join(" ") }));
    setShowNewPlayer(true);
  };

  const handleCreatePlayer = async () => {
    setError("");
    const required = { first_name: "First name", last_name: "Last name", graduation_year: "Graduation year", primary_position: "Primary position", province: "Province" };
    const missing = Object.entries(required).filter(([k]) => !String(np[k] || "").trim()).map(([, l]) => l);
    if (missing.length) { setError(`Please complete: ${missing.join(", ")}`); return; }
    setBusy(true);
    try {
      const raw = {
        ...np,
        graduation_year: np.graduation_year ? Number(np.graduation_year) : null,
      };
      // Strip empty-string optional values so Pydantic Literal validators accept null
      const payload = Object.fromEntries(
        Object.entries(raw).filter(([, v]) => v !== "" && v !== undefined)
      );
      const { data } = await api.post("/players", payload);
      setMyPlayers((prev) => [...prev, data]);
      selectPlayer(data);
      setShowNewPlayer(false);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitWaiver = async () => {
    setError("");
    if (!waiver.signer_name || !waiver.signature_text || !waiver.agreed) {
      setError("Please complete all waiver fields and accept the terms.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/registrations", {
        camp_id: camp.id,
        player_id: selectedPlayerId,
        waiver: {
          signer_name: waiver.signer_name,
          signer_relationship: waiver.signer_relationship,
          signature_text: waiver.signature_text,
          agreed_at: new Date().toISOString(),
          content_version: "v1.0",
        },
        emergency_contact_name: waiver.emergency_contact_name,
        emergency_contact_phone: waiver.emergency_contact_phone,
      });
      setRegistrationId(data.id);
      setStep(3);
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleCheckout = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        registration_id: registrationId,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      setError(formatApiError(e?.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  if (!camp) {
    return (
      <PublicLayout>
        <div className="max-w-3xl mx-auto p-10 label-overline">Loading camp…</div>
      </PublicLayout>
    );
  }

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white py-10 border-b-4 border-[#E63946]">
        <div className="max-w-4xl mx-auto px-6">
          <Link to={`/camps/${slug}`} className="label-overline text-white/60 hover:text-white">
            ← Back to camp
          </Link>
          <h1 data-testid="registration-page-title" className="font-display text-4xl uppercase tracking-tighter mt-3">
            Register for {camp.title}
          </h1>
          <div className="text-sm text-white/70 mt-2">
            {camp.city}, {camp.province} · ${camp.price} {camp.currency}
          </div>
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-12">
        <div className="max-w-4xl mx-auto px-6">
          {/* Stepper */}
          <div className="flex items-center mb-10 gap-2">
            {[
              { n: 1, label: "Player" },
              { n: 2, label: "Waiver" },
              { n: 3, label: "Payment" },
            ].map((s, i) => (
              <React.Fragment key={s.n}>
                <div className="flex items-center gap-2">
                  <div
                    className={`w-8 h-8 grid place-items-center rounded-sm text-xs font-bold ${
                      step >= s.n ? "bg-[#0A192F] text-white" : "bg-white border border-slate-300 text-slate-500"
                    }`}
                  >
                    {step > s.n ? <Check className="w-4 h-4" /> : s.n}
                  </div>
                  <span className={`text-xs font-semibold uppercase tracking-wider ${step >= s.n ? "text-[#0A192F]" : "text-slate-400"}`}>
                    {s.label}
                  </span>
                </div>
                {i < 2 && <div className={`flex-1 h-px ${step > s.n ? "bg-[#0A192F]" : "bg-slate-300"}`} />}
              </React.Fragment>
            ))}
          </div>

          {error && (
            <div data-testid="registration-error" className="border-l-4 border-[#E63946] bg-red-50 p-3 text-sm text-[#E63946] mb-4">
              {error}
            </div>
          )}

          <div className="bg-white border border-slate-200 rounded-sm p-8">
            {/* Step 1: Player */}
            {step === 1 && (
              <div>
                <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2"><Users className="w-5 h-5 text-[#E63946]" /> Select Player</h2>
                <p className="text-sm text-slate-600 mt-2">Choose one of your players, search for an existing player, or create a new one.</p>

                {/* Your Players */}
                {myPlayers.length > 0 && (
                  <div className="mt-6" data-testid="your-players-section">
                    <div className="label-overline text-slate-500 mb-2">Your Players</div>
                    <div className="space-y-2">
                      {myPlayers.map((p) => (
                        <button key={p.id} type="button" data-testid={`registration-player-${p.id}`} onClick={() => selectPlayer(p)}
                          className={`w-full flex items-center gap-3 p-3 border rounded-sm text-left transition-colors ${selectedPlayerId === p.id ? "border-[#0A192F] bg-slate-50 ring-1 ring-[#0A192F]" : "border-slate-200 hover:border-[#0A192F]"}`}>
                          <PlayerAvatar p={p} />
                          <div className="min-w-0 flex-1">
                            <div className="font-display text-lg uppercase truncate">{p.first_name} {p.last_name}</div>
                            <div className="text-xs text-slate-500 truncate">{[p.primary_position, p.graduation_year, p.province, p.current_team].filter(Boolean).join(" · ") || "Details TBD"}</div>
                          </div>
                          {selectedPlayerId === p.id && <Check className="w-5 h-5 text-[#0A192F] shrink-0" />}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Search for another existing player */}
                <div className="mt-6" data-testid="player-search-section">
                  <div className="label-overline text-slate-500 mb-2">Search for another existing player</div>
                  <div className="relative">
                    <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                    <input data-testid="player-search-input" value={searchQ} onChange={(e) => setSearchQ(e.target.value)}
                      placeholder="Name, grad year, position, province or team…"
                      className="w-full pl-9 pr-3 py-3 border border-slate-300 rounded-sm text-sm focus:outline-none focus:border-[#0A192F]" />
                  </div>
                  {searchQ.trim().length >= 2 && (
                    <div className="mt-3 space-y-2" data-testid="player-search-results">
                      {searching && <div className="text-sm text-slate-400 py-2">Searching…</div>}
                      {!searching && searchResults && searchResults.length === 0 && (
                        <div data-testid="player-search-empty" className="flex items-center justify-between gap-3 p-4 border border-dashed border-slate-300 rounded-sm">
                          <span className="text-sm text-slate-500">No matching player found.</span>
                          <button type="button" data-testid="search-create-new-btn" onClick={openCreateFromSearch} className="text-sm font-semibold text-[#E63946] uppercase tracking-wider inline-flex items-center gap-1 shrink-0"><UserPlus className="w-4 h-4" /> Create New Player</button>
                        </div>
                      )}
                      {!searching && (searchResults || []).map((r) => (
                        <div key={r.id} data-testid={`search-result-${r.id}`} className={`flex items-center gap-3 p-3 border rounded-sm ${selectedPlayerId === r.id ? "border-[#0A192F] bg-slate-50" : "border-slate-200"}`}>
                          <PlayerAvatar p={r} />
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold truncate">{r.first_name} {r.last_name}</div>
                            <div className="text-xs text-slate-500 truncate">{[r.primary_position, r.graduation_year, r.province, r.current_team].filter(Boolean).join(" · ") || "—"}</div>
                            {r.claimed_by_other && <div data-testid={`search-claimed-${r.id}`} className="text-[11px] text-amber-600 mt-0.5 flex items-center gap-1"><ShieldAlert className="w-3 h-3 shrink-0" /> This profile already exists. Request access or contact CBR.</div>}
                          </div>
                          {r.selectable ? (
                            <button type="button" data-testid={`search-select-${r.id}`} onClick={() => selectPlayer(r)} className="shrink-0 px-3 py-2 bg-[#0A192F] text-white text-xs font-semibold uppercase tracking-wider rounded-sm hover:bg-[#1a2a4a]">{selectedPlayerId === r.id ? "Selected" : "Select"}</button>
                          ) : (
                            <span className="shrink-0 text-[11px] text-slate-400 uppercase tracking-wider">Locked</span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Create New Player */}
                {!showNewPlayer ? (
                  <button data-testid="registration-add-player-button" onClick={() => setShowNewPlayer(true)}
                    className="mt-6 w-full p-4 border border-dashed border-slate-300 text-sm font-semibold uppercase tracking-wider hover:border-[#0A192F] inline-flex items-center justify-center gap-2 rounded-sm"><UserPlus className="w-4 h-4" /> Create New Player</button>
                ) : (
                  <div className="mt-6" data-testid="new-player-form">
                    <div className="label-overline text-slate-500 mb-2">Create New Player</div>
                    <div className="grid sm:grid-cols-2 gap-4">
                      <input data-testid="new-player-first-name" placeholder="First name *" value={np.first_name} onChange={(e) => setNp({ ...np, first_name: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <input data-testid="new-player-last-name" placeholder="Last name *" value={np.last_name} onChange={(e) => setNp({ ...np, last_name: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <input data-testid="new-player-grad-year" type="number" placeholder="Graduation year * (e.g. 2028)" value={np.graduation_year} onChange={(e) => setNp({ ...np, graduation_year: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <select data-testid="new-player-position" value={np.primary_position} onChange={(e) => setNp({ ...np, primary_position: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm bg-white">
                        <option value="">Primary Position *</option>
                        {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <select data-testid="new-player-province" value={np.province} onChange={(e) => setNp({ ...np, province: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm bg-white">
                        <option value="">Province *</option>
                        {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <select data-testid="new-player-secondary-position" value={np.secondary_position} onChange={(e) => setNp({ ...np, secondary_position: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm bg-white">
                        <option value="">Secondary Position (optional)</option>
                        {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
                      </select>
                      <select value={np.bats} onChange={(e) => setNp({ ...np, bats: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm bg-white">
                        <option value="">Bats (optional)</option>
                        <option value="R">Right</option><option value="L">Left</option><option value="S">Switch</option>
                      </select>
                      <select value={np.throws} onChange={(e) => setNp({ ...np, throws: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm bg-white">
                        <option value="">Throws (optional)</option>
                        <option value="R">Right</option><option value="L">Left</option>
                      </select>
                      <input data-testid="new-player-dob" type="date" value={np.date_of_birth} onChange={(e) => setNp({ ...np, date_of_birth: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm" />
                      <input placeholder="School (optional)" value={np.school} onChange={(e) => setNp({ ...np, school: e.target.value })} className="px-4 py-3 border border-slate-300 rounded-sm" />
                    </div>
                    <div className="mt-4">
                      <div className="label-overline text-slate-500 mb-1">Current Team (optional)</div>
                      <TeamSelector value={np.current_team_id} onChange={(t) => setNp({ ...np, current_team_id: t?.id || "", current_team: t?.name || "" })} testidPrefix="np-team" />
                    </div>
                    <label className="mt-3 flex items-center gap-2 text-xs text-slate-600">
                      <input data-testid="new-player-show-age" type="checkbox" checked={np.show_age_publicly} onChange={(e) => setNp({ ...np, show_age_publicly: e.target.checked })} />
                      Show age (not birthdate) on public profile. Off by default; DOB is always private.
                    </label>
                    <div className="flex gap-3 mt-4">
                      <button onClick={() => setShowNewPlayer(false)} className="px-5 py-3 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">Cancel</button>
                      <button data-testid="save-new-player-button" onClick={handleCreatePlayer} disabled={busy} className="px-5 py-3 bg-[#0A192F] text-white text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60">{busy ? "Saving…" : "Create & Select"}</button>
                    </div>
                  </div>
                )}

                <div className="mt-8 flex justify-end">
                  <button
                    data-testid="step1-next-button"
                    disabled={!selectedPlayerId}
                    onClick={() => setStep(2)}
                    className="px-6 py-3 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-40 inline-flex items-center gap-2"
                  >
                    Next: Waiver <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 2: Waiver */}
            {step === 2 && (
              <div>
                <h2 className="font-display text-2xl uppercase tracking-tight">Digital Waiver & Emergency Contact</h2>
                <p className="text-sm text-slate-600 mt-2">Required for all CBR camp registrations.</p>

                <div className="mt-6 p-5 bg-slate-50 border border-slate-200 max-h-56 overflow-y-auto text-sm text-slate-700 leading-relaxed">
                  <div className="font-semibold mb-2">CBR Prospects Participation Waiver (v1.0)</div>
                  <p>
                    I, the parent or legal guardian of the registered player, acknowledge that participation in baseball
                    camp and showcase activities involves inherent risks of injury. I hereby release Canadian Baseball
                    Rankings (CBR), CBR Prospects, its staff, evaluators, partners, and host venues from any and all
                    claims arising from such participation.
                  </p>
                  <p className="mt-2">
                    I authorize CBR staff to obtain emergency medical care for the participant if required. I consent
                    to the collection and publication of verified performance metrics, photographs, video footage,
                    and scouting evaluations on the CBR Prospects platform and associated public-facing channels.
                  </p>
                  <p className="mt-2">
                    Refund policy: Registrations are refundable up to 14 days prior to the event start date, less a
                    10% processing fee.
                  </p>
                </div>

                <div className="grid sm:grid-cols-2 gap-4 mt-6">
                  <div className="sm:col-span-2">
                    <label className="label-overline">Signer Full Name (Parent/Guardian)</label>
                    <input data-testid="waiver-signer-name" value={waiver.signer_name} onChange={(e) => setWaiver({ ...waiver, signer_name: e.target.value })} className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-sm" />
                  </div>
                  <div>
                    <label className="label-overline">Relationship</label>
                    <select data-testid="waiver-relationship" value={waiver.signer_relationship} onChange={(e) => setWaiver({ ...waiver, signer_relationship: e.target.value })} className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-sm bg-white">
                      <option value="parent">Parent</option>
                      <option value="guardian">Legal Guardian</option>
                      <option value="self">Player (18+)</option>
                    </select>
                  </div>
                  <div>
                    <label className="label-overline">Type Full Name as Signature</label>
                    <input data-testid="waiver-signature" value={waiver.signature_text} onChange={(e) => setWaiver({ ...waiver, signature_text: e.target.value })} className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-sm italic font-serif" />
                  </div>
                  <div>
                    <label className="label-overline">Emergency Contact Name</label>
                    <input data-testid="emergency-name" value={waiver.emergency_contact_name} onChange={(e) => setWaiver({ ...waiver, emergency_contact_name: e.target.value })} className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-sm" />
                  </div>
                  <div>
                    <label className="label-overline">Emergency Contact Phone</label>
                    <input data-testid="emergency-phone" value={waiver.emergency_contact_phone} onChange={(e) => setWaiver({ ...waiver, emergency_contact_phone: e.target.value })} className="mt-2 w-full px-4 py-3 border border-slate-300 rounded-sm" />
                  </div>
                </div>

                <label className="mt-6 flex items-start gap-3 cursor-pointer">
                  <input data-testid="waiver-agree-checkbox" type="checkbox" checked={waiver.agreed} onChange={(e) => setWaiver({ ...waiver, agreed: e.target.checked })} className="mt-1" />
                  <span className="text-sm text-slate-700">
                    I have read, understood, and agree to the CBR Prospects participation waiver and refund policy.
                  </span>
                </label>

                <div className="mt-8 flex justify-between">
                  <button onClick={() => setStep(1)} className="px-5 py-3 border border-slate-300 text-sm font-semibold uppercase tracking-wider rounded-sm">
                    ← Back
                  </button>
                  <button
                    data-testid="step2-next-button"
                    onClick={handleSubmitWaiver}
                    disabled={busy}
                    className="px-6 py-3 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60 inline-flex items-center gap-2"
                  >
                    {busy ? "Saving…" : "Continue to Payment"} <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Payment */}
            {step === 3 && (
              <div>
                <h2 className="font-display text-2xl uppercase tracking-tight">Secure Payment</h2>
                <p className="text-sm text-slate-600 mt-2">Pay via Stripe. No card data is stored by CBR Prospects.</p>

                <div className="mt-8 grid sm:grid-cols-2 gap-4">
                  <div className="border border-slate-200 p-5">
                    <div className="label-overline">Event</div>
                    <div className="font-semibold mt-1 text-sm">{camp.title}</div>
                  </div>
                  <div className="border border-slate-200 p-5">
                    <div className="label-overline">Total</div>
                    <div className="font-data text-3xl mt-1 text-[#0A192F]">${camp.price} <span className="text-base">{camp.currency}</span></div>
                  </div>
                </div>

                <button
                  data-testid="checkout-button"
                  onClick={handleCheckout}
                  disabled={busy}
                  className="mt-8 w-full bg-[#E63946] hover:bg-[#D90429] text-white py-4 text-sm font-semibold uppercase tracking-wider rounded-sm disabled:opacity-60"
                >
                  {busy ? "Redirecting to Stripe…" : "Pay with Stripe"}
                </button>
                <p className="text-[11px] text-slate-500 mt-3 text-center">
                  You will be redirected to a secure Stripe checkout page.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}


function PlayerAvatar({ p }) {
  const initials = `${(p.first_name || "?")[0] || ""}${(p.last_name || "")[0] || ""}`.toUpperCase();
  return (
    <div className="w-11 h-11 rounded-sm bg-slate-100 overflow-hidden grid place-items-center shrink-0">
      {p.profile_photo_url
        ? <img src={p.profile_photo_url} alt="" className="w-full h-full object-cover" />
        : <span className="text-xs font-bold text-slate-400">{initials || "?"}</span>}
    </div>
  );
}
